import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { formatEur, type CashFlowEntry, type MarkPaidIntent, type MoneyMode } from '@/lib/cashFlow';
import { payrollNetEntryMonth, type LoanRequestOptions } from '@/lib/loans';
import { DateText } from '@/components/ui/DateText';
import { docCell, docTypeBadgeCls } from '@/lib/ledgerCells';
import { settlementView } from '@/lib/settlementDetail';
import { SettlementDetail } from '@/components/cash-flow/settlement/SettlementDetail';
import { SettlementSources } from '@/components/cash-flow/settlement/SettlementSources';
import { MarkPaidPanel } from '@/components/cash-flow/settlement/MarkPaidPanel';
import { PayrollSettlePane } from '@/components/loans/PayrollSettlePane';

/**
 * LEFT pane of the enriched-entry detail (module 1.3): the read-only facts of an
 * entry — identity, parties, dates, the Neto/KM/Bruto money breakdown and the
 * settlement (arveldus). Every figure is integer cents read from the entry (L9):
 * this component NEVER computes an amount; the settlement view + finance-core own
 * the math. The NET/GROSS toggle (v1.4) only highlights which money row is the
 * active display — KM stays DATA, never a display mode.
 */

/** `null` (no value on the document) renders as an em dash, not an empty cell. */
function orDash(value: string | null): string {
  return value ?? '—';
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">{label}</span>
      <span className="text-[13px] text-foreground">{children}</span>
    </div>
  );
}

function MoneyRow({
  label,
  cents,
  active,
  sign,
}: {
  label: string;
  cents: number;
  active: boolean;
  sign: string;
}) {
  return (
    <div className={cn('flex items-center justify-between rounded px-2 py-1', active && 'bg-[hsl(var(--vatmode))]/10')}>
      <span className="text-[12px] text-muted-foreground">{label}</span>
      <span
        className={cn(
          'tabular-nums',
          active ? 'text-[15px] font-semibold text-[hsl(var(--vatmode))]' : 'text-[13px] text-foreground/80',
        )}
      >
        {`${sign}${formatEur(cents)}`}
      </span>
    </div>
  );
}

export function EntryFactsPane({
  entry,
  today,
  mode,
  options,
  onMarkPaid,
  markPaidPending = false,
  onRemoveManual,
  removeManualPending,
}: {
  entry: CashFlowEntry;
  today: string;
  mode: MoneyMode;
  /** Auth/fetch plumbing for the payroll settle pane's own query hooks. */
  options?: LoanRequestOptions;
  /** "Märgi laekunuks/tasutuks" from the Arveldus section — dispatches the shared
   *  mark-paid intent (date + optional partial); the caller mints the idempotency
   *  key and runs the mutation. The SAME function the register popover exposes
   *  (L14: one shared MarkPaidPanel, two lens-appropriate mounts). Offered only
   *  while the entry has an outstanding gap and is not a payroll net-wage row. */
  onMarkPaid?: (intent: MarkPaidIntent) => void;
  /** The mark-paid mutation is in flight — locks the panel's confirm button. */
  markPaidPending?: boolean;
  /** "Tühista käsitsi makse" — the mark-paid UNDO: retracts the entry's Manuaal
   *  settlement events (mis-click). Offered on the Arvelduse allikad Manuaal row
   *  only while a manual fact exists; the caller runs the mutation. */
  onRemoveManual?: () => void;
  /** The remove-manual mutation is in flight — locks the confirm strip. */
  removeManualPending?: boolean;
}) {
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const isIncome = entry.kind === 'income';
  const sign = isIncome ? '' : '−';
  // The "üle tähtaja" cue on the due date reads the SAME finance-core settlement
  // status as every other lens (via the settlement view this pane already builds),
  // so a final invoice settled by a received prepayment is NOT flagged overdue here
  // — never the raw, date-only isOverdue flag (L9/L14; Task 2).
  const settlement = settlementView(entry, today, false);
  // `payroll:<M>:net` → the month whose per-employee settle pane replaces the
  // generic settlement block below (ONE shared detector, lib/loans).
  const payrollMonth = payrollNetEntryMonth(entry.externalId);
  // The same colored document-type badge the register shows before every doc number
  // (docCell code + docTypeBadgeCls) so this drill header identifies the document at a
  // glance. Statutory / loan rows carry no docType → no badge (dc.code null).
  const dc = docCell(entry.docType);

  return (
    <div className="flex flex-col gap-3">
      {/* identity — the label tooltip rides the row wrapper (as the register does),
          so the colored code badge stays a pure glyph and carries no dead branch. */}
      <div
        className="flex items-center gap-2"
        title={dc.label ? `${dc.label} · ${orDash(entry.docNumber)}` : orDash(entry.docNumber)}
      >
        <span className={isIncome ? 'text-[hsl(var(--income))]' : 'text-[hsl(var(--cost))]'} aria-hidden>
          {isIncome ? '▲' : '▼'}
        </span>
        {dc.code && (
          <span
            className={cn(
              'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              docTypeBadgeCls(entry.docType),
            )}
          >
            {dc.code}
          </span>
        )}
        <span className="font-mono text-lg text-foreground">{orDash(entry.docNumber)}</span>
        <span className="text-[13px] text-muted-foreground">{entry.party}</span>
      </div>

      {/* facts */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border border-border bg-card/40 px-3 py-3">
        <Field label="Osapool">{entry.party}</Field>
        <Field label="Dokumendi kuupäev">
          <DateText iso={entry.documentDate} />
        </Field>
        <Field label="Maksetähtaeg">
          <DateText
            iso={entry.expectedDate}
            className={cn(settlement.status === 'overdue' && 'text-[hsl(var(--cost))]')}
          />
        </Field>
        <Field label="Suund">{isIncome ? 'Tulu (laekumine)' : 'Kulu (väljaminek)'}</Field>
      </div>

      {/* money — finance-core (L9), the view never computes */}
      {!entry.kmdSettlement && <section className="rounded-lg border border-border bg-card/40">
        <header className="flex items-center gap-2 border-b border-border/70 px-3 py-2">
          <span className="text-[hsl(var(--vatmode))]">€</span>
          <h3 className="text-[12px] font-semibold uppercase tracking-wide text-foreground">Summa</h3>
          <span className="ml-auto text-[10px] text-muted-foreground/70">finance-core (L9) · vaade ei arvuta</span>
        </header>
        <div className="flex flex-col gap-0.5 px-3 py-3">
          <MoneyRow label="Neto" cents={entry.money.net} active={mode === 'net'} sign={sign} />
          <div className="flex items-center justify-between rounded px-2 py-1">
            <span className="text-[12px] text-muted-foreground/70">
              Käibemaks <span className="ml-1 text-[10px]">({entry.money.vatRate}%)</span>
            </span>
            <span className="tabular-nums text-[13px] text-muted-foreground/70">
              {`${sign}${formatEur(entry.money.vat)}`}
            </span>
          </div>
          <MoneyRow label="Bruto" cents={entry.money.gross} active={mode === 'gross'} sign={sign} />
        </div>
        <p className="px-3 pb-3 text-[10px] text-muted-foreground/60">
          NET/GROSS lülitab vaid kuvatava väärtuse — KM jääb andmeks (v1.4).
        </p>
      </section>}

      {/* settlement (arveldus) — derived in finance-core / lib, not here. A
          payroll net-wage entry (`payroll:<M>:net`) swaps in the per-employee
          settle surface, while a KMD entry swaps in KmdSettlementPanel below.
          Both own additive payment history, so generic source-priority details,
          mark-paid and bulk manual-payment undo would contradict or bypass them. */}
      {!entry.kmdSettlement && <section className="rounded-lg border border-border bg-card/40">
        <header className="flex items-center gap-2 border-b border-border/70 px-3 py-2">
          <h3 className="text-[12px] font-semibold uppercase tracking-wide text-foreground">Arveldus</h3>
          {payrollMonth && (
            <span className="ml-auto text-[10px] text-muted-foreground/70">töötajate kaupa</span>
          )}
        </header>
        {/* text-[13px] pulls the settlement figures (Summa/Tasutud/Jääk/Tähtaeg) down to
            the SAME size as the Summa block's Neto/KM/Bruto rows — one visual scale for
            every money figure in the facts pane. (SettlementSources sets its own sizes.) */}
        <div className="flex flex-col gap-3 px-3 py-3 text-[13px]">
          {payrollMonth ? (
            <PayrollSettlePane month={payrollMonth} today={today} options={options} />
          ) : (
            <>
              <SettlementDetail view={settlement} />
              <SettlementSources
                entry={entry}
                onRemoveManual={onRemoveManual}
                removeManualPending={removeManualPending}
              />
              {/* Manual mark-paid — the detail lens's mount of the ONE shared
                  MarkPaidPanel (L14; the register popover mounts the same panel).
                  Gated on a real outstanding gap; a settled entry offers nothing. */}
              {onMarkPaid && settlement.outstanding > 0 && (
                <div className="flex flex-col gap-1.5">
                  {markPaidOpen ? (
                    <MarkPaidPanel
                      outstandingCents={settlement.outstanding}
                      isIncome={isIncome}
                      defaultDate={today}
                      pending={markPaidPending}
                      onConfirm={(intent) => {
                        onMarkPaid(intent);
                        setMarkPaidOpen(false);
                      }}
                      onCancel={() => setMarkPaidOpen(false)}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setMarkPaidOpen(true)}
                      className="self-start rounded border border-[hsl(var(--income))]/50 px-2 py-1 text-[12px] font-medium text-[hsl(var(--income))] transition-colors hover:bg-[hsl(var(--income))]/10"
                    >
                      {isIncome ? 'Märgi laekunuks' : 'Märgi tasutuks'}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>}
    </div>
  );
}
